import React, { Component, useMemo, useState } from 'react';
import { View, StyleSheet, FlatList } from 'react-native';
import { useMutation, useQuery } from '@tanstack/react-query';

import { MarketEditModalContent } from './MarketEditor/MarketEditModalContent';
import MarketListItem from './MarketEditor/MarketListItem';

import { Text, TextInputSearch, Page, Icon } from './uiold';
import { themeColors } from './uiold/Constants';
import { canUser } from '../support/shared/permissionHelpers';
import { useAuthState } from './AuthState';
import { confirm } from './App/AppDialog';

import {
  LoadingIndicator,
  Modal,
  ModalOverlay,
  useShowMessage,
  Button,
} from '../core-ui';
import { Market } from '../types/shared/Market';
import { User } from '../types/shared/User';
import { useFetchJson } from '../support/useFetch';
import DataStore from '../support/DataStore';
import IndexedList from '../support/shared/IndexedList';
import { UpdateMarket } from '../runtypes/MarketRuntype';

const dataStore = DataStore.getInstance('app');

type Props = {
  user: User;
  markets: Map<string, Market>;
  onSave: (market: Market) => void;
  onAddMarketPress: () => void;
  onDeletePress: (market: Market) => void;
  selectedMarketId: string | null;
  setSelectedMarketId: (id: string | null) => void;
};

type State = {
  filterText: string;
};

class MarketEditorView extends Component<Props, State> {
  state: State = {
    filterText: '',
  };

  render() {
    let {
      user,
      markets,
      onSave,
      onAddMarketPress,
      onDeletePress,
      selectedMarketId,
      setSelectedMarketId,
    } = this.props;
    let { filterText } = this.state;
    let canEdit = canUser(user, { marketsAndPricing: 'WRITE' });
    let selectedMarket = selectedMarketId
      ? markets.get(selectedMarketId)
      : undefined;
    let filteredMarkets = filterMarkets(markets, filterText);
    let onClose = () => setSelectedMarketId(null);

    return (
      <Page>
        <Page.Header title={t('Markets and Dates')}>
          {canEdit ? (
            <Button variant="primary" onClick={() => onAddMarketPress()}>
              {t('Add Market')}
            </Button>
          ) : null}
        </Page.Header>
        <View style={styles.body}>
          <View style={styles.marketList}>
            <View style={styles.searchControl}>
              <TextInputSearch
                autoFocus={true}
                data-testid="market-search-input"
                value={filterText}
                onChangeText={(text) => this.setState({ filterText: text })}
              />
            </View>
            {markets.size > 0 ? (
              <FlatList
                data={filteredMarkets}
                data-testid="market-list"
                keyExtractor={(item) => item.id}
                renderItem={({ item }) => (
                  <MarketListItem
                    isSelected={selectedMarketId === item.id}
                    market={item}
                    onPress={() => {
                      if (selectedMarketId == null) {
                        setSelectedMarketId(item.id);
                      }
                    }}
                  />
                )}
              />
            ) : (
              <Text type="label" style={styles.noMarketText}>
                {t('No market available.')}
              </Text>
            )}
          </View>
          <View style={styles.marketForm}>
            <View style={styles.emptyRightPane}>
              <Icon name="event" size="XXL" color={themeColors.blue} />
              <Text>
                {t(
                  'Select market from the list, or click the "Add Market" button above.',
                )}
              </Text>
            </View>
          </View>
        </View>
        <Modal
          size="2xl"
          scrollBehavior="inside"
          isOpen={selectedMarket != null}
          onClose={onClose}
          closeOnOverlayClick={false}
          closeOnEsc={false}
        >
          <ModalOverlay />
          {selectedMarket ? (
            <MarketEditModalContent
              canEdit={canEdit}
              market={selectedMarket}
              onSave={(market) => {
                onSave(market);
                onClose();
              }}
              onClose={onClose}
              onDeletePress={(market) => {
                onDeletePress(market);
                onClose();
              }}
            />
          ) : null}
        </Modal>
      </Page>
    );
  }
}

function filterMarkets(markets: Map<string, Market>, filterText: string) {
  let searchText = filterText.toLowerCase().trim();
  let filtered: Array<Market> = [];
  for (let [_id, market] of markets) {
    if (!searchText || marketMatches(market, searchText)) {
      filtered.push(market);
    }
  }
  filtered = filtered.sort((a, b) => a.name.localeCompare(b.name));
  return filtered;
}

function marketMatches(market: Market, searchText: string) {
  let value = typeof market.name === 'string' ? market.name : '';
  if (value && value.toLowerCase().includes(searchText)) {
    return true;
  }
  return false;
}

let styles = StyleSheet.create({
  marketList: {
    width: '38%',
    marginRight: 20,
    borderRadius: 5,
    overflow: 'hidden',
    backgroundColor: themeColors.white,
  },
  marketForm: {
    flex: 1,
    borderRadius: 5,
    justifyContent: 'center',
    backgroundColor: themeColors.white,
  },
  body: {
    flex: 1,
    flexDirection: 'row',
  },
  searchControl: {
    paddingTop: 20,
    paddingBottom: 21,
    paddingHorizontal: 10,
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderColor: themeColors.borderGrey,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    backgroundColor: themeColors.white,
  },
  noMarketText: {
    padding: 20,
    textAlign: 'center',
  },
  emptyRightPane: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 30,
    marginBottom: 20,
  },
});

export default function MarketEditor() {
  const { user } = useAuthState();
  const showMessage = useShowMessage();
  const fetchJson = useFetchJson();

  const [selectedMarketId, setSelectedMarketId] = useState<string | null>(null);

  // TODO: Move this to a custom hook in a shared file
  const { data, error, refetch } = useQuery(
    ['markets'],
    () =>
      fetchJson(`/api/markets`, null, (data) => data.markets as Array<Market>),
    {
      onSuccess: (markets) => {
        // For legacy reasons, update the data store after successful fetch
        dataStore.set('markets', new IndexedList(markets));
      },
    },
  );

  const { mutate: createMarket } = useMutation(
    ['POST:/api/markets'],
    (name: string) => fetchJson('/api/markets', { body: { name } }),
    {
      onSuccess: (data) => {
        showMessage('success', t('Market created successfully'));
        void refetch();
        setSelectedMarketId(data.marketId);
      },
      onError: (error) => {
        showMessage('error', t('Error creating market: {error}', { error }));
      },
    },
  );

  const { mutate: updateMarket } = useMutation(
    ['POST:/api/markets/:id'],
    (data: { id: string; updates: UpdateMarket }) => {
      const { id, updates } = data;
      return fetchJson(`/api/markets/${id}`, { body: { updates } });
    },
    {
      onSuccess: () => {
        showMessage('success', t('Saved successfully'));
        void refetch();
      },
      onError: (error) => {
        showMessage('error', t('Error saving changes: {error}', { error }));
      },
    },
  );

  const { mutate: deleteMarket } = useMutation(
    ['POST:/api/markets/:id/delete'],
    (id: string) => fetchJson(`/api/markets/${id}/delete`, { body: {} }),
    {
      onSuccess: () => {
        showMessage('success', t('Market deleted successfully'));
        void refetch();
      },
      onError: (error) => {
        showMessage('error', t('Error deleting market: {error}', { error }));
      },
    },
  );

  const marketsMap = useMemo(() => {
    if (!data) {
      return undefined;
    }
    const marketsMap = new Map<string, Market>();
    for (const market of data) {
      marketsMap.set(market.id, market);
    }
    return marketsMap;
  }, [data]);

  if (error) {
    return <p>{String(error)}</p>;
  }

  if (!marketsMap) {
    return <LoadingIndicator />;
  }

  return (
    <MarketEditorView
      markets={marketsMap}
      user={user}
      onSave={(market) => {
        const { id, name, description, dates, num_spots, hidden } = market;
        updateMarket({
          id,
          updates: { name, description, dates, num_spots, hidden },
        });
      }}
      onAddMarketPress={() => {
        createMarket(t('New Market'));
      }}
      onDeletePress={async (market) => {
        const proceed = await confirm(
          t('Deleted Market?'),
          t('Are you sure you want to permanently delete this market?'),
        );
        if (proceed) {
          deleteMarket(market.id);
        }
      }}
      selectedMarketId={selectedMarketId}
      setSelectedMarketId={setSelectedMarketId}
    />
  );
}
